// The rules of batch-tracked stock (Drugs, Medical) that the browser needs, kept
// free of React and the DOM so they can be pinned down by stockRules.test.js.
//
// The server is the authority on every one of them that changes anything — a short
// count's reason, what a holding may be moved to, what's left after a move (see
// backend/app/core/stock.py). These exist so the pages can say so before a request
// is made, and so the kit check, the stock report and the history agree with the
// inventory page about what "expired" and "short" mean.
import { itemLabel as composeItemLabel } from "./kitList";
import { STRICT_STOCK_CATEGORIES, typeLabel } from "./constants";

// --- Dates and expiry -----------------------------------------------------------------

// Local YYYY-MM-DD (avoids UTC off-by-one from toISOString).
export const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const todayISO = () => isoDate(new Date());
export const isoInDays = (days, from = new Date()) => {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  return isoDate(d);
};

// Expiry highlighting: red once the date has passed, amber while it's close.
export const EXPIRY_SOON_DAYS = 30;

// The printed date shifted by the type's grace in whole years, which is the date the
// flags actually key off. Built through Date so a shifted 29 February normalises to a
// real day rather than to a date string that doesn't exist.
export const graceDate = (iso, grace) => {
  if (!iso || !grace) return iso || "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y) return iso;
  return isoDate(new Date(y + grace, m - 1, d));
};

// The verdict on one expiry date, or null when there is nothing to say: the colour
// to paint it and the sentence explaining why, kept together because every caller
// needs both. With a grace in play the message names the printed date and the day
// the grace runs out, since "expired" and "flagged" are then different things and a
// bare colour would look like a mistake. `today` is for the tests.
export const expiryFlag = (iso, grace = 0, today = todayISO()) => {
  const at = graceDate(iso, grace);
  if (!at) return null;
  const [y, m, d] = today.split("-").map(Number);
  const soon = isoInDays(EXPIRY_SOON_DAYS, new Date(y, m - 1, d));
  const severity = at < today ? "error" : at <= soon ? "warning" : null;
  if (!severity) return null;
  if (!grace) {
    return {
      severity,
      message: severity === "error" ? "Expired" : `Expires within ${EXPIRY_SOON_DAYS} days`,
    };
  }
  const period = `${grace}-year grace`;
  return {
    severity,
    message: severity === "error"
      ? `Expired ${iso} — ${period} ran out ${at}`
      : `Expired ${iso} — ${period} ends ${at}`,
  };
};

// --- Names and places ---------------------------------------------------------------

// An item's name, as the inventory page shows it.
export const itemName = (item, typeById) =>
  composeItemLabel(item, typeLabel(typeById[item.type_id]));

// The containers an item sits inside, outermost first, guarding against a cycle.
export const containersOf = (item, itemById) => {
  const path = [];
  const seen = new Set([item.id]);
  let cur = item.location_id ? itemById[item.location_id] : null;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift(cur);
    cur = cur.location_id ? itemById[cur.location_id] : null;
  }
  return path;
};

// "CAS Box 1 / Drugs pouch" — where an item is.
export const whereLabel = (item, itemById, typeById) =>
  containersOf(item, itemById).map(c => itemName(c, typeById)).join(" / ");

// "Paracetamol (CAS Box 1 / Drugs pouch)" — an item and where it is, the way every
// ledger line names a place (place_label in core/stock.py).
export const placeLabel = (item, itemById, typeById) => {
  const where = whereLabel(item, itemById, typeById);
  const name = itemName(item, typeById);
  return where ? `${name} (${where})` : name;
};

// "CAS Box 1 / Drugs pouch" including the container itself — what a container picker
// shows, so two pouches called the same thing can be told apart.
export const containerPath = (container, itemById, typeById) =>
  [...containersOf(container, itemById), container].map(c => itemName(c, typeById)).join(" / ");

// The stock role an item plays itself — its type's ("Stock cupboard", "Pending
// disposal"), since the role is set once on the type. "store", "quarantine" or null.
export const roleOf = (item, typeById) => typeById[item.type_id]?.stock_role || null;

// The stock role a container plays, including by being inside one: a pouch inside
// the cupboard is cupboard stock. "store", "quarantine" or null.
export const stockRoleOf = (item, itemById, typeById) => {
  const chain = [item, ...containersOf(item, itemById).reverse()];
  return chain.map(c => roleOf(c, typeById)).find(Boolean) || null;
};

// --- Holdings -----------------------------------------------------------------------

// Every holding across the inventory, each with the item it sits at — the source list
// for moving, disposing and replenishing. A holding is one batch at one item (served
// as that item's `batches`; id is the holding's).
export const allHoldings = (items) =>
  items.flatMap(item => (item.batches || []).map(h => ({ ...h, item })));

// Earliest expiry first — first expiring, first out — with undated stock last and the
// smaller holding first among equals, so topping up empties the odds and ends.
export const fefo = (holdings) => [...holdings].sort((a, b) => {
  const ea = a.expiry_date || "9999-12-31";
  const eb = b.expiry_date || "9999-12-31";
  if (ea !== eb) return ea < eb ? -1 : 1;
  return (a.quantity - b.quantity) || (a.id - b.id);
});

// How many a line is short of what it should hold, or 0.
export const shortfall = (item) =>
  item.required_quantity != null
    ? Math.max(0, item.required_quantity - (item.quantity ?? 0))
    : 0;

// A replenishment plan: take `need` from `sources` in FEFO order, as move lines.
// Expired stock is never suggested — it goes to disposal, not into a bag.
export const replenishPlan = (sources, need, { grace = 0, today = todayISO() } = {}) => {
  const lines = [];
  let left = need;
  for (const h of fefo(sources)) {
    if (left <= 0) break;
    if (h.quantity <= 0 || h.recalled) continue;
    if (expiryFlag(h.expiry_date, grace, today)?.severity === "error") continue;
    const take = Math.min(left, h.quantity);
    lines.push({ holding_id: h.id, quantity: take });
    left -= take;
  }
  return { lines, unmet: Math.max(0, left) };
};

// --- Checks -------------------------------------------------------------------------

export const isStrictCategory = (category) => STRICT_STOCK_CATEGORIES.includes(category);

// What the server will make of one counted batch (core/stock.check): a match is a
// plain "check"; for a strict category any difference is an "adjust" that needs a
// reason and is flagged for investigation; for a relaxed one a short count is
// "consume" (assumed used) and needs nothing, and finding more is an "adjust" whose
// reason is optional.
export const checkVerdict = (expected, counted, strict) => {
  if (counted === "" || counted == null || Number.isNaN(Number(counted))) {
    return { kind: null, needsReason: false, investigate: false };
  }
  const n = Number(counted);
  if (n === expected) return { kind: "check", needsReason: false, investigate: false };
  if (strict) return { kind: "adjust", needsReason: true, investigate: true };
  return n < expected
    ? { kind: "consume", needsReason: false, investigate: false }
    : { kind: "adjust", needsReason: false, investigate: false };
};

// --- History --------------------------------------------------------------------------

// The words for a ledger line's kind (stock_movements.kind).
export const KIND_LABEL = {
  receive: "Received",
  move: "Moved",
  quarantine: "Set aside for disposal",
  dispose: "Disposed of",
  check: "Checked",
  adjust: "Count corrected",
  consume: "Used (assumed)",
  administer: "Given to patient",
  void: "Drug record voided",
  open: "Unit opened",
  issue: "Issued with container",
  return: "Returned with container",
  opening_balance: "Opening balance",
};
export const KIND_OPTIONS = Object.keys(KIND_LABEL);

// A signed change as it reads in a table: "+5", "−2", "" for none.
export const signed = (n) => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : "");
